import {
  organizationSkillImportSchema,
  organizationSkillLocalScanRequestSchema,
  organizationSkillProjectScanRequestSchema,
  type OrganizationSkillDetail,
  type OrganizationSkillFileDetail,
  type OrganizationSkillImportResult,
  type OrganizationSkillListItem,
  type OrganizationSkillLocalScanResult,
  type OrganizationSkillProjectScanResult,
} from "@rudderhq/shared";
import { Command } from "commander";
import { getAgentCliCapabilityById } from "../../agent-v1-registry.js";
import {
  addCommonClientOptions,
  formatInlineRecord,
  handleCommandError,
  printOutput,
  resolveCommandContext,
  type BaseClientOptions,
} from "./common.js";
import { formatExamplesAndCautions } from "./help.js";

interface SkillListOptions extends BaseClientOptions {
  orgId?: string;
}

export function searchOrganizationSkills(rows: OrganizationSkillListItem[], query: string): OrganizationSkillListItem[] {
  const normalized = query.trim().toLocaleLowerCase("en-US");
  if (!normalized) return [];
  return rows.filter((row) => [
    row.id,
    row.key,
    row.slug,
    row.name,
    row.description,
    row.sourceType,
    row.sourceLocator,
    row.sourceRef,
    row.sourceLabel,
    row.sourceBadge,
    row.sourcePath,
  ].some((value) => typeof value === "string" && value.toLocaleLowerCase("en-US").includes(normalized)));
}

interface SkillImportOptions extends BaseClientOptions {
  orgId?: string;
  source: string;
}

interface SkillFileOptions extends BaseClientOptions {
  orgId?: string;
  path?: string;
}

interface SkillScanLocalOptions extends BaseClientOptions {
  orgId?: string;
  roots?: string;
}

interface SkillScanProjectsOptions extends BaseClientOptions {
  orgId?: string;
  projectIds?: string;
  workspaceIds?: string;
}

export function registerSkillCommands(program: Command): void {
  const skill = program.command("skill").description("Organization skill library operations");

  addCommonClientOptions(
    skill
      .command("list")
      .description(getAgentCliCapabilityById("skill.list").description)
      .option("-O, --org-id <id>", "Organization ID")
      .action(async (opts: SkillListOptions) => {
        try {
          const ctx = resolveCommandContext(opts, { requireCompany: true });
          const rows = (await ctx.api.get<OrganizationSkillListItem[]>(`/api/orgs/${ctx.orgId}/skills`)) ?? [];

          if (ctx.json) {
            printOutput(rows, { json: true });
            return;
          }

          if (rows.length === 0) {
            printOutput([], { json: false });
            return;
          }

          for (const row of rows) {
            console.log(
              formatInlineRecord({
                id: row.id,
                key: row.key,
                slug: row.slug,
                name: row.name,
                sourceBadge: row.sourceBadge,
                compatibility: row.compatibility,
                attachedAgentCount: row.attachedAgentCount,
              }),
            );
          }
        } catch (err) {
          handleCommandError(err);
        }
      }),
    { includeCompany: false },
  );

  addCommonClientOptions(
    skill
      .command("search")
      .description(getAgentCliCapabilityById("skill.search").description)
      .argument("<query>", "Skill name, slug, description, source, or selection key")
      .option("-O, --org-id <id>", "Organization ID")
      .action(async (query: string, opts: SkillListOptions) => {
        try {
          const ctx = resolveCommandContext(opts, { requireCompany: true });
          const rows = (await ctx.api.get<OrganizationSkillListItem[]>(`/api/orgs/${ctx.orgId}/skills`)) ?? [];
          printOutput(searchOrganizationSkills(rows, query), { json: ctx.json });
        } catch (err) {
          handleCommandError(err);
        }
      }),
    { includeCompany: false },
  );

  addCommonClientOptions(
    skill
      .command("get")
      .description(getAgentCliCapabilityById("skill.get").description)
      .argument("<skillId>", "Skill ID")
      .option("-O, --org-id <id>", "Organization ID")
      .action(async (skillId: string, opts: SkillListOptions) => {
        try {
          const ctx = resolveCommandContext(opts, { requireCompany: true });
          const row = await ctx.api.get<OrganizationSkillDetail>(`/api/orgs/${ctx.orgId}/skills/${skillId}`);
          printOutput(row, { json: ctx.json });
        } catch (err) {
          handleCommandError(err);
        }
      }),
    { includeCompany: false },
  );

  addCommonClientOptions(
    skill
      .command("file")
      .description(getAgentCliCapabilityById("skill.file").description)
      .argument("<skillId>", "Skill ID")
      .option("-O, --org-id <id>", "Organization ID")
      .option("--path <path>", "Skill package file path", "SKILL.md")
      .addHelpText("after", formatExamplesAndCautions({
        examples: [
          {
            description: "Read the main skill trigger and workflow instructions:",
            command: "rudder skill file <skill-uuid> --path SKILL.md --org-id <org-id>",
          },
          {
            description: "Inspect a specific reference file in the skill package:",
            command: "rudder skill file <skill-uuid> --path references/usage.md --json",
          },
        ],
        cautions: [
          "Prefer the skill UUID for slashful keys such as local/<hash>/<slug>.",
          "Read SKILL.md before assuming a skill's trigger or workflow behavior.",
        ],
      }))
      .action(async (skillId: string, opts: SkillFileOptions) => {
        try {
          const ctx = resolveCommandContext(opts, { requireCompany: true });
          const query = new URLSearchParams({ path: opts.path ?? "SKILL.md" });
          const row = await ctx.api.get<OrganizationSkillFileDetail>(
            `/api/orgs/${ctx.orgId}/skills/${skillId}/files?${query.toString()}`,
          );
          printOutput(row, { json: ctx.json });
        } catch (err) {
          handleCommandError(err);
        }
      }),
    { includeCompany: false },
  );

  addCommonClientOptions(
    skill
      .command("import")
      .description(getAgentCliCapabilityById("skill.import").description)
      .option("-O, --org-id <id>", "Organization ID")
      .requiredOption("--source <source>", "Skill source (local path, URL, or repo ref)")
      .addHelpText("after", formatExamplesAndCautions({
        examples: [
          {
            description: "Import a durable organization-shared skill package:",
            command: "rudder skill import --org-id <org-id> --source /abs/path/to/skill --json",
          },
          {
            description: "List imported skills afterward to get stable ids:",
            command: "rudder skill list --org-id <org-id> --json",
          },
        ],
        cautions: [
          "Import organization-shared skills from durable shared paths, not disposable agent-private work directories.",
          "Importing a skill does not automatically enable it for every agent; update agent skill selections separately.",
        ],
      }))
      .action(async (opts: SkillImportOptions) => {
        try {
          const ctx = resolveCommandContext(opts, { requireCompany: true });
          const payload = organizationSkillImportSchema.parse({ source: opts.source });
          const result = await ctx.api.post<OrganizationSkillImportResult>(`/api/orgs/${ctx.orgId}/skills/import`, payload);
          printOutput(result, { json: ctx.json });
        } catch (err) {
          handleCommandError(err);
        }
      }),
    { includeCompany: false },
  );

  addCommonClientOptions(
    skill
      .command("scan-local")
      .description(getAgentCliCapabilityById("skill.scan-local").description)
      .option("-O, --org-id <id>", "Organization ID")
      .option("--roots <csv>", "Comma-separated local roots to scan")
      .action(async (opts: SkillScanLocalOptions) => {
        try {
          const ctx = resolveCommandContext(opts, { requireCompany: true });
          const payload = organizationSkillLocalScanRequestSchema.parse({
            roots: parseCsv(opts.roots),
          });
          const result = await ctx.api.post<OrganizationSkillLocalScanResult>(
            `/api/orgs/${ctx.orgId}/skills/scan-local`,
            payload,
          );
          printOutput(result, { json: ctx.json });
        } catch (err) {
          handleCommandError(err);
        }
      }),
    { includeCompany: false },
  );

  addCommonClientOptions(
    skill
      .command("scan-projects")
      .description(getAgentCliCapabilityById("skill.scan-projects").description)
      .option("-O, --org-id <id>", "Organization ID")
      .option("--project-ids <csv>", "Comma-separated project IDs")
      .option("--workspace-ids <csv>", "Comma-separated workspace IDs")
      .action(async (opts: SkillScanProjectsOptions) => {
        try {
          const ctx = resolveCommandContext(opts, { requireCompany: true });
          const payload = organizationSkillProjectScanRequestSchema.parse({
            projectIds: parseCsv(opts.projectIds),
            workspaceIds: parseCsv(opts.workspaceIds),
          });
          const result = await ctx.api.post<OrganizationSkillProjectScanResult>(
            `/api/orgs/${ctx.orgId}/skills/scan-projects`,
            payload,
          );
          printOutput(result, { json: ctx.json });
        } catch (err) {
          handleCommandError(err);
        }
      }),
    { includeCompany: false },
  );
}

function parseCsv(value: string | undefined): string[] | undefined {
  if (!value) return undefined;
  const rows = value.split(",").map((entry) => entry.trim()).filter(Boolean);
  return rows.length > 0 ? rows : undefined;
}
